import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';


export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormError('');
      return;
    }

    const url = 'https://readdy.ai/api/form/d9qasl7nahb7382ov8u0';

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const teamValues = [
    {
      icon: 'ri-heart-line',
      title: '用心陪伴',
      desc: '我们理解大胸姐妹在日常生活中的真实困扰，每一篇文章都从实际体验出发，提供真正有用的建议。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '专业可靠',
      desc: '所有内容均参考内衣行业专业知识，结合大量真实用户反馈，确保每一条建议都经得起验证。',
    },
    {
      icon: 'ri-group-line',
      title: '社区互助',
      desc: '我们致力于构建一个温暖的大胸姐妹交流社区，在这里你可以放心讨论那些"只有我们懂"的话题。',
    },
  ];

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      label: '电子邮箱',
      value: 'hello@bigbustguide.com',
    },
    {
      icon: 'ri-wechat-line',
      label: '微信公众号',
      value: '大胸姐妹指南',
    },
    {
      icon: 'ri-red-packet-line',
      label: '小红书',
      value: '@大胸姐妹指南',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Organization"
        data={{
          name: '大胸姐妹指南',
          url: `${window.location.origin}${__BASE_PATH__}/about`,
          description: '专为大胸姐妹打造的内衣选购与穿搭知识平台，致力于提供最专业、最贴心的内衣指南。',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'hello@bigbustguide.com',
          },
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 大胸姐妹指南',
          url: `${window.location.origin}${__BASE_PATH__}/about`,
          inLanguage: 'zh-CN',
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />
              <div className="mt-6">
                <h1 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                  关于我们
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                  了解大胸姐妹指南背后的故事与初心
                </p>
              </div>
            </div>
          </div>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div>
                  <h2 className="text-lg md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                    我们的故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      大胸姐妹指南创立于2024年，由一群深受内衣选购困扰的大胸女性发起。我们深知，对于D杯以上的姐妹来说，找到一件合身、舒适又好看的内衣有多难——勒痕、肩带滑落、空杯、压胸……这些问题几乎每天都在困扰我们。
                    </p>
                    <p>
                      市面上大多数内衣品牌和内容都在为B-C杯的"标准身材"服务，大胸姐妹的需求长期被忽视。我们决定改变这个现状，用专业的知识和真实的体验，为大胸姐妹提供真正有用的内衣选购与穿搭指南。
                    </p>
                    <p>
                      从罩杯测量到品牌测评，从肩带选择到穿搭技巧，我们覆盖了大胸姐妹在内衣选择中的每一个环节。我们相信，每一位大胸姐妹都值得拥有舒适、自信的穿着体验。
                    </p>
                  </div>
                </div>
                <div className="w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20cozy%20workspace%20with%20elegant%20fashion%20editorial%20mood%20board%2C%20soft%20natural%20lighting%2C%20cream%20and%20blush%20tones%2C%20minimalist%20feminine%20aesthetic%2C%20creative%20studio%20atmosphere%2C%20clean%20artistic%20composition&width=800&height=600&seq=about-story-2026&orientation=landscape"
                    alt="关于我们"
                    title="大胸姐妹指南团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-lg md:text-2xl font-heading font-semibold text-foreground-900 mb-3">
                  我们的理念
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                  三个核心价值观，指引我们每一篇文章的创作方向
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
                {teamValues.map((item) => (
                  <div
                    key={item.title}
                    className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 text-center"
                  >
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-100 text-accent-600">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-lg md:text-2xl font-heading font-semibold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                  来自大胸姐妹们的真实反馈
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                <div className="bg-background-100 rounded-lg border border-background-200/70 p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "作为一个F杯的女生，终于找到一个真正懂大胸姐妹的平台了！文章写得太实用了，照着尺码测量指南重新量了一下，才发现穿了两年错误的尺码……"
                  </p>
                  <p className="text-xs text-foreground-500">— 小雨，28岁，上海</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/70 p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "运动内衣那篇文章真的救了我！之前跑步胸晃得难受，按照文章的建议换了高强度运动内衣，现在跑10公里都不怕了。"
                  </p>
                  <p className="text-xs text-foreground-500">— Lily，32岁，深圳</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/70 p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "品牌推荐太良心了！按文章推荐尝试了奶糖派，第一次穿到钢圈不压、肩带不勒的内衣，感动到哭。希望你们越做越好！"
                  </p>
                  <p className="text-xs text-foreground-500">— 阿欣，25岁，广州</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
                <div>
                  <h2 className="text-lg md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                    有任何问题、建议或合作意向，欢迎通过以下方式联系我们。也欢迎分享你的大胸内衣故事，你的经历可能会帮助到更多姐妹。
                  </p>
                  <div className="flex flex-col gap-4">
                    {contactInfo.map((info) => (
                      <div key={info.label} className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                          <i className={`${info.icon} text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">{info.label}</p>
                          <p className="text-sm font-medium text-foreground-900">{info.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">
                    给我们留言
                  </h3>
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form="about-contact">
                    <input
                      type="text"
                      name="name"
                      placeholder="你的名字"
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="你的邮箱"
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                    <textarea
                      name="message"
                      placeholder="你想说的话……"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      required
                    ></textarea>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-anti-spider"
                    />
                    <button
                      type="submit"
                      className="w-full px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">
                        留言已发送，我们会尽快回复你！
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}